import Oidc, { User } from "oidc-client";
import { ref, h, watch } from "vue";
import * as constants from "@/constants";
import useGlobalLoading from "./useGlobalLoading";
import { Router } from "vue-router";
import useUser from "./useUser";

const oidcManager = ref<Oidc.UserManager | null>(null);
const user = ref(null as User | null);

export default function () {
  async function initialize(
    authority: string,
    clientId: string,
    router: Router
  ) {
    useGlobalLoading().startLoading();
    oidcManager.value = new Oidc.UserManager({
      authority: authority,
      client_id: clientId,
      redirect_uri: `${window.location.origin}${constants.OIDC_CALLBACK_URI}`,
      response_type: "code",
      popup_post_logout_redirect_uri: constants.OIDC_LOGOUT_URI,
      scope: "openid",
      monitorSession: false,
      automaticSilentRenew: true,
      post_logout_redirect_uri: `${window.location.origin}`,
      userStore: new Oidc.WebStorageStateStore({
        store: window.sessionStorage,
      }),
    });

    user.value = await oidcManager.value?.getUser();
    handleManagerEvents(oidcManager.value, router);
    setupRouter(router);
    if (user.value) {
      useGlobalLoading().stopLoading();
    }
  }

  function handleManagerEvents(mgr: Oidc.UserManager, router: Router) {
    mgr.events.addUserLoaded((newUser) => {
      user.value = newUser;
    });

    mgr.events.addUserUnloaded(() => {
      user.value = null;
    });

    mgr.events.addAccessTokenExpired(() => {
      user.value = null;
      // router.push("/loggedout");
    });

    mgr.events.addUserSignedOut(() => {
      user.value = null;
    });

    // Handle silent renew errors (expired/revoked refresh token)
    mgr.events.addSilentRenewError((error) => {
      console.error(
        "Silent renew error - refresh token may be expired or revoked:",
        error
      );

      // Clear the user session
      user.value = null;

      // Remove user from storage
      mgr
        .removeUser()
        .then(() => {
          // Force re-signin with current route as redirect target
          const currentRoute = router.currentRoute.value;
          mgr
            .signinRedirect({
              state: { to: currentRoute },
            })
            .catch((err) => {
              console.error("Failed to redirect to signin:", err);
            });
        })
        .catch((err) => {
          console.error("Failed to remove user:", err);
        });
    });
  }

  function setupRouter(router: Router) {
    router.addRoute({
      path: constants.OIDC_CALLBACK_URI,
      meta: {
        isPublic: true,
      },
      component: {
        render: () => h("div"),
        created() {
          oidcManager.value?.signinRedirectCallback().then((data) => {
            const redirect = data.state ? data.state.to : null;
            watch(
              () => useUser().user.value,
              (newValue) => {
                if (newValue != null) {
                  if (newValue.isSuperAdmin) {
                    router.replace(redirect || "/");
                  } else if (newValue.isAdmin) {
                    // User is not super-admin, and should therefore be redirected to his organization, or if trying to reach a resource without access see error.
                    router.replace(
                      redirect.path == "/organizations"
                        ? newValue.userHomePage
                        : redirect
                    );
                  } else if (newValue.isUserAdmin) {
                    // User is not super-admin, and should therefore be redirected to users page, or if trying to reach a resource without access see error.
                    router.replace(
                      redirect.path == "/users"
                        ? newValue.userHomePage
                        : redirect
                    );
                  } else if (newValue.isApplicationAdmin) {
                    // User is not super-admin, and should therefore be redirected applications page, or if trying to reach a resource without access see error.
                    router.replace(
                      redirect.path == "/applications"
                        ? newValue.userHomePage
                        : redirect
                    );
                  } else {
                    router.replace(newValue.userHomePage);
                  }
                  useGlobalLoading().stopLoading();
                }
              },
              { immediate: true }
            );
          });
        },
      },
    });

    router.addRoute({
      path: constants.OIDC_LOGOUT_URI,
      meta: {
        isPublic: true,
      },
      component: () => import("@/views/LoggedOut.vue"),
    });

    router.beforeEach(async (to, from, next) => {
      if (to.matched[0]?.meta?.isPublic) {
        useGlobalLoading().stopLoading();
        return next();
      } else if (user.value && !user.value.expired) {
        return next();
      } else {
        const result = await oidcManager.value?.signinRedirect({
          state: { to },
        });
        if (result) {
          return next();
        } else {
          return next(false);
        }
      }
    });
  }

  function logOut() {
    const token = user.value?.id_token;
    oidcManager.value?.signoutRedirect({ id_token_hint: token });
  }

  return {
    initialize,
    logOut,
    user,
  };
}
